import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Color } from "@evergis/color";

import { ColorScaleField } from "../../../ColorScaleField/ColorScaleField";
import { ClassifyColorInput } from "./ClassifyColorInput";
import { ColorContainer } from "../../styled";
import { CLUSTER_CLASSIFY_FILL_COLOR_ATTRIBUTE } from "../ClusterSymbolEditor/consts";
import { getParameterValue, isParameterByAttribute } from "../../../../evergis/core/classification/getFromClassified";
import { useRemoveClassification } from "../../hooks/useRemoveClassification";
import { useClassificationEdit } from "../../../Classification/hooks/useClassificationEdit";
import { useClassesRecalc } from "../../../Classification/hooks/useClassesRecalc";
import { useSegmentStyle } from "../../../../modules/Style/hooks/useSegmentStyle";
import { ClassifyAble, isClusterFillColor, ParameterByAttribute } from "../../../../interfaces/style";
import { ClassificationParameter } from "../../../../interfaces/classification";
import { ClassificationQuery } from "../../../../ducks/location/location";

interface ColorClassifierProps {
  name: ClassificationParameter;
  value: ClassifyAble<string>;
  valueIndex?: number;
  disabled?: boolean;
  error?: boolean;
  classifyAble?: boolean;
  onChange: (value: Color) => void;
}

export const ColorClassifier: FC<ColorClassifierProps> = ({
  name,
  value,
  disabled,
  error,
  valueIndex,
  classifyAble = true,
  onChange,
}) => {
  const { t } = useTranslation("common");
  const parameterValue = getParameterValue(value);
  const editClassification = useClassificationEdit(name, value, valueIndex);
  const segmentStyle = useSegmentStyle();
  const removeClassification = useRemoveClassification(onChange, new Color(parameterValue));
  const { calculateClassesRange } = useClassesRecalc();

  const onClick = useCallback(() => {
    const newParams: ClassificationQuery = { parameter: name };

    if (isClusterFillColor(segmentStyle.symbol, name)) {
      newParams.field = CLUSTER_CLASSIFY_FILL_COLOR_ATTRIBUTE;
    }

    if (valueIndex !== undefined) {
      newParams.valueIndex = valueIndex;
    }

    calculateClassesRange(newParams);
  }, [calculateClassesRange, name, segmentStyle.symbol, valueIndex]);

  return isParameterByAttribute(value) ? (
    <ColorContainer>
      <ColorScaleField
        iconKind={"close"}
        buttonAction={removeClassification}
        colors={getColorsSet(value)}
        width={"5.625rem"}
        disabled={disabled}
        error={error}
        onColorClick={editClassification}
        tooltipProps={{
          content: t("symbolEditor.removeClassifier"),
          children: null,
        }}
      />
    </ColorContainer>
  ) : (
    <ClassifyColorInput
      color={parameterValue}
      disabled={disabled}
      classifyAble={classifyAble}
      onChange={onChange}
      onClick={onClick}
    />
  );
};

function getColorsSet(param: ParameterByAttribute<string>): Color[] {
  return param.values.map(({ value }) => new Color(value));
}
